import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AirIcon from "@mui/icons-material/Air";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import RadarIcon from "@mui/icons-material/Radar";
import AgricultureIcon from "@mui/icons-material/Agriculture";

import _ from "lodash";

import { useNavigate } from "react-router";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import MapIcon from "@mui/icons-material/Map";
import MailIcon from "@mui/icons-material/Mail";

const drawerWidth = 240;

const LINKS = [
  [
    { label: "Farms", icon: <FormatListBulletedIcon />, href: "/farms" },
    { label: "Manage Fields", icon: <MapIcon />, href: "/farms/manage" },
    { label: "Analysis", icon: <AgricultureIcon />, href: "/farms/analysis" },
    { label: "My Invitations", icon: <MailIcon />, href: "/invitations" },
  ],
  [{ label: "Circulations", icon: <AirIcon />, href: "/circulations" }],
  [{ label: "Scan", icon: <RadarIcon />, href: "/scan" }],
  // [{ label: "Crop Forecast", icon: <RadarIcon />, href: "/crop-forecast" }], TODO: regional forecast page Paul - Ibrahim
];

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function MiniDrawer({
  children,
  isAuthenticated,
  logout,
  loginWithRedirect,
  farms,
  activeFarm = null,
  onSelectActiveFarm = null,
  user = null,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleActiveFarmChange = (event) => {
    if (onSelectActiveFarm !== null) {
      const farm = _.find(farms, (f) => f.id === event.target.value);
      onSelectActiveFarm(farm);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          {isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={[{ marginRight: 5 }, open && { display: "none" }]}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{ fontWeight: 900, display: "flex", alignItems: "center" }}
            >
              <Box component="span" sx={{ color: "#163729" }}>
                Pasture
              </Box>

              <Box
                component="span"
                sx={{ color: "secondary.main", ml: 1, fontWeight: 800 }}
              >
                Flow
              </Box>
            </Typography>
          </Box>

          {isAuthenticated && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
              }}
            >
              {farms?.length > 0 && (
                <FormControl fullWidth>
                  <Select
                    value={activeFarm ? activeFarm.id : "no-active"}
                    label="Farm"
                    onChange={handleActiveFarmChange}
                    variant="standard"
                  >
                    {farms.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name}
                      </MenuItem>
                    ))}
                    {!activeFarm && (
                      <MenuItem key={"no-active"} value={"no-active"}>
                        No Farm Selected
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar alt={user.name} src={user.picture} />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography>{user.name}</Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography>{user.email}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => logout({ returnTo: window.location.origin })}
                >
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}

          {!isAuthenticated && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => loginWithRedirect()}
            >
              Log In
            </Button>
          )}
        </Toolbar>
      </AppBar>
      {isAuthenticated && (
        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {LINKS.map((group) => {
              return (
                <Box key={`group-${group[0].label}`}>
                  {group.map(({ label, icon, href }) => (
                    <ListItem
                      key={label}
                      disablePadding
                      sx={{ display: "block" }}
                    >
                      <ListItemButton
                        onClick={() => navigate(href)}
                        sx={[
                          { minHeight: 48, px: 2.5 },
                          open
                            ? { justifyContent: "initial" }
                            : { justifyContent: "center" },
                        ]}
                      >
                        <ListItemIcon
                          sx={[
                            { minWidth: 0, justifyContent: "center" },
                            open ? { mr: 3 } : { mr: "auto" },
                          ]}
                        >
                          {icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  <Divider />
                </Box>
              );
            })}
          </List>
        </Drawer>
      )}
      <Box className="appbar-main-box" component="main" sx={{ flexGrow: 1 }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}
